import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  FlaskConical, Check, ArrowRight, Upload, Activity, Heart,
  Beaker, Droplet, Dna, Microscope, Pill, Wind,
} from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/App";
import SyncVaultButton from "@/components/SyncVaultButton";  // iter908 — closes retest loop

/**
 * LabCoveragePage — единна страница на /labs.
 *  • Публичен изглед: 42 marker badges, 8 групи, CTA "Регистрирай се за пълно покритие"
 *  • Auth user изглед: прогрес ring (X/42 covered), per-marker checks + "Качи →"
 *
 * iter704 — превръща fragmentation в acquisition funnel.
 */

const GROUP_ICONS = {
  glucose_insulin: Activity,
  lipids: Heart,
  inflammation: Wind,
  vitamins: Pill,
  thyroid: Beaker,
  reproductive: Droplet,
  dutch: Microscope,
  dna: Dna,
};

const GROUP_COLOR = {
  glucose_insulin: "rose",
  lipids: "amber",
  inflammation: "orange",
  vitamins: "emerald",
  thyroid: "sky",
  reproductive: "pink",
  dutch: "violet",
  dna: "indigo",
};

const COLOR_PALETTE = {
  rose:    { bg: "bg-rose-500/10",    text: "text-rose-600 dark:text-rose-300",    pill: "bg-rose-100 dark:bg-rose-500/20"  },
  amber:   { bg: "bg-amber-500/10",   text: "text-amber-600 dark:text-amber-300",  pill: "bg-amber-100 dark:bg-amber-500/20"  },
  orange:  { bg: "bg-orange-500/10",  text: "text-orange-600 dark:text-orange-300",pill: "bg-orange-100 dark:bg-orange-500/20"},
  emerald: { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-300", pill: "bg-emerald-100 dark:bg-emerald-500/20" },
  sky:     { bg: "bg-sky-500/10",     text: "text-sky-600 dark:text-sky-300",      pill: "bg-sky-100 dark:bg-sky-500/20"      },
  pink:    { bg: "bg-pink-500/10",    text: "text-pink-600 dark:text-pink-300",    pill: "bg-pink-100 dark:bg-pink-500/20"    },
  violet:  { bg: "bg-violet-500/10",  text: "text-violet-600 dark:text-violet-300",pill: "bg-violet-100 dark:bg-violet-500/20"},
  indigo:  { bg: "bg-indigo-500/10",  text: "text-indigo-600 dark:text-indigo-300",pill: "bg-indigo-100 dark:bg-indigo-500/20"},
};

export default function LabCoveragePage() {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAuth = !!user;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const userId = user?._id || user?.id;
        const url = isAuth && userId
          ? `${API}/labs/v2/${userId}/coverage`
          : `${API}/labs/v2/canonical-markers/public`;
        const { data } = await axios.get(url);
        if (alive) setData(data);
      } catch (_) {
        // Fall back to public if auth call fails
        try {
          const { data } = await axios.get(`${API}/labs/v2/canonical-markers/public`);
          if (alive) setData(data);
        } catch (_) { /* swallow */ }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [API, isAuth, user]);

  const totalMarkers = data?.total || data?.total_markers || 42;
  const coveredCount = data?.covered_count || 0;
  const coveragePct = data?.coverage_pct ?? 0;
  const groups = data?.groups || [];

  const cardCls = isDark
    ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]"
    : "bg-white border-gray-200";

  return (
    <div className={`min-h-screen pt-16 pb-12 ${isDark ? "bg-[hsl(222,47%,7%)] text-white" : "bg-gradient-to-b from-blue-50/30 to-white text-gray-900"}`} data-testid="lab-coverage-page">
      {/* ─── HERO ────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-12">
        <div className="flex flex-col lg:flex-row items-start gap-8">
          <div className="flex-1">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-4 ${
              isDark ? "bg-blue-500/20 text-blue-300" : "bg-blue-100 text-blue-700"
            }`}>
              <FlaskConical className="w-3.5 h-3.5" />
              42 канонични маркера · 8 панела
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {isAuth
                ? "Вашето лабораторно покритие"
                : <>Лабораторни маркери, които<br/><span className="text-blue-600 dark:text-blue-400">Nutramilo разчита</span></>
              }
            </h1>
            <p className={`text-base sm:text-lg ${isDark ? "text-gray-300" : "text-gray-600"} max-w-2xl mb-6`}>
              {isAuth
                ? "Виж кои маркери са активни в Вашия профил и кои липсват. Колкото повече покрити маркери, толкова по-точни са Health Twin изчисленията Ви."
                : "Платформата интегрира резултати от обикновени лабораторни тестове, DUTCH панели, тиреоиден панел и ДНК варианти в единна canonical база. Качете Вашите резултати и получете персонализирани препоръки от 18 Дигитални Близнаци."}
            </p>
            {!isAuth && (
              <div className="flex flex-wrap gap-3">
                <Link to="/register" data-testid="lab-coverage-cta-register"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition shadow-lg shadow-blue-500/20">
                  Започни сега · 14 дни безплатно <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/login"
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition ${
                    isDark ? "bg-white/10 hover:bg-white/20" : "bg-gray-100 hover:bg-gray-200 text-gray-900"
                  }`}>
                  Вече имам акаунт
                </Link>
              </div>
            )}
          </div>

          {/* Coverage ring (auth) OR static count (public) */}
          {isAuth ? (
            <div className={`${cardCls} border rounded-3xl p-8 w-full lg:w-[320px] text-center`} data-testid="lab-coverage-ring">
              <CoverageRing pct={coveragePct} isDark={isDark} />
              <div className="mt-4">
                <div className="text-3xl font-extrabold">{coveredCount}<span className="text-lg opacity-50">/{totalMarkers}</span></div>
                <div className="text-xs opacity-60 mt-1">маркери покрити</div>
              </div>
              {totalMarkers - coveredCount > 0 && (
                <button onClick={() => navigate("/analyses")} data-testid="lab-coverage-upload-cta"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition">
                  <Upload className="w-4 h-4" /> Качи нови резултати
                </button>
              )}
              {/* iter908 — closes the Daily Check-in retest → Vault refresh loop */}
              <div className="mt-3 flex justify-center">
                <SyncVaultButton
                  isDark={isDark}
                  lang="bg"
                  variant="ghost"
                  testid="lab-coverage-sync-vault"
                />
              </div>
            </div>
          ) : (
            <div className={`${cardCls} border rounded-3xl p-8 w-full lg:w-[320px]`}>
              <div className="text-center">
                <div className="text-6xl font-extrabold text-blue-600 dark:text-blue-400">42</div>
                <div className="text-sm opacity-60 mt-1">канонични маркера</div>
                <div className={`mt-4 pt-4 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
                  <div className="text-3xl font-bold">18</div>
                  <div className="text-sm opacity-60 mt-1">Digital Twins</div>
                </div>
                <div className={`mt-4 pt-4 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
                  <div className="text-3xl font-bold">75+</div>
                  <div className="text-sm opacity-60 mt-1">Cross-twin cascade rules</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── GROUPS ──────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6">
        {loading ? (
          <div className="text-center opacity-50 py-12">Зареждане…</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="lab-coverage-groups">
            {groups.map((g) => {
              const Icon = GROUP_ICONS[g.group_id] || FlaskConical;
              const color = GROUP_COLOR[g.group_id] || "indigo";
              const palette = COLOR_PALETTE[color];
              const pct = g.total > 0 ? Math.round((g.covered_count / g.total) * 100) : 0;
              return (
                <div
                  key={g.group_id}
                  className={`${cardCls} border rounded-2xl p-5`}
                  data-testid={`group-${g.group_id}`}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl ${palette.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-6 h-6 ${palette.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold">{g.label_bg}</h3>
                      <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {isAuth
                          ? `${g.covered_count}/${g.total} покрити · ${pct}%`
                          : `${g.total} маркера`
                        }
                      </div>
                    </div>
                  </div>

                  {/* Markers grid */}
                  <ul className="flex flex-wrap gap-1.5" data-testid={`group-markers-${g.group_id}`}>
                    {g.markers.map((m) => (
                      <li
                        key={m.code}
                        title={`${m.label_bg}${m.unit ? ` (${m.unit})` : ""}`}
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
                          m.covered
                            ? "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                            : isAuth
                              ? `${palette.pill} ${palette.text} opacity-50 hover:opacity-100 cursor-pointer`
                              : `${palette.pill} ${palette.text}`
                        }`}
                        data-testid={`marker-${m.code}`}
                        onClick={() => { if (isAuth && !m.covered) navigate("/analyses"); }}
                      >
                        {m.covered ? <Check className="w-3 h-3" /> : null}
                        <span>{m.label_bg}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom CTA */}
        <div className={`mt-10 p-6 rounded-2xl border ${cardCls} text-center`} data-testid="lab-coverage-bottom-cta">
          <h3 className="text-xl font-bold mb-2">
            {isAuth
              ? totalMarkers - coveredCount > 0
                ? `Качи Вашите резултати — ${totalMarkers - coveredCount} маркера остават`
                : "Перфектно покритие! ✓"
              : "Готови сте за персонализиран Health Twin?"}
          </h3>
          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"} mb-4 max-w-xl mx-auto`}>
            {isAuth
              ? "Колкото повече показатели, толкова по-точна е оценката на 20-те близнака."
              : "Качете лабораторни резултати, добавете BI-RADS от мамографски доклад, и AI обединява всичко в персонална превенция."}
          </p>
          {isAuth ? (
            <button onClick={() => navigate("/analyses")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition">
              <Upload className="w-4 h-4" /> Към лабораторни анализи
            </button>
          ) : (
            <Link to="/register" data-testid="lab-coverage-bottom-register"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition">
              Регистрирай се <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}

function CoverageRing({ pct, isDark }) {
  const R = 56;
  const C = 2 * Math.PI * R;
  const offset = C - (Math.min(100, Math.max(0, pct)) / 100) * C;
  return (
    <svg width="140" height="140" viewBox="0 0 140 140" className="mx-auto">
      <circle cx="70" cy="70" r={R}
        stroke={isDark ? "rgba(255,255,255,0.08)" : "#E5E7EB"} strokeWidth="10" fill="none" />
      <circle cx="70" cy="70" r={R}
        stroke="url(#cov-grad)" strokeWidth="10" fill="none"
        strokeDasharray={C} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transform: "rotate(-90deg)", transformOrigin: "center", transition: "stroke-dashoffset 0.8s ease" }} />
      <defs>
        <linearGradient id="cov-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
      </defs>
      <text x="70" y="78" textAnchor="middle" fontSize="28" fontWeight="800" fill={isDark ? "#fff" : "#111827"}>
        {Math.round(pct)}%
      </text>
    </svg>
  );
}
